<template>
  <c-modal v-if="site" class="edit-client" :title="title" size="lg" :show="show" @update:show="toggle" :close-on-backdrop="false">
    <template v-if="cloneStarted">
      <c-row>
        <c-col col="12" md="6">
          <p class="lead mb-3">We are about to clone site <strong>{{site.siteAddress}}</strong> to <strong>{{ sftpCredentials.host }}</strong>!</p>
          <p class="lead mb-3">It will take a little while - go make yourself a coffee, accompanied with a few of <em>the best</em> Australian chocolate biscuits, the <a href="https://en.wikipedia.org/wiki/Tim_Tam" target="_blank">Tim Tam</a>.</p>
          <p class="lead mb-3">The Koala will send you an email when the clone starts, and when the clone has finished.</p>
        </c-col>
        <c-col col="12" md="6">
          <c-img src="/gif/clone.gif" width="100%" shape="rounded"/>
        </c-col>
      </c-row>
    </template>
    <template v-else>
      <c-row>
        <c-col sm="12" :md="checkDone || isSending ? 6 : 12">
          <c-form>
            <c-input
                v-model="sftpCredentials.host"
                label="SFTP Host"
                type="text"
                placeholder="your-host.com"
                :lazy="false"
                :is-valid="checkIfValid('host')"
                invalid-feedback="Please enter a host"
                :disabled="isSending"
            />
            <c-input
                v-model="sftpCredentials.port"
                label="SFTP Port"
                type="text"
                placeholder="22"
                :lazy="false"
                :is-valid="checkIfValid('port')"
                invalid-feedback="Please enter a port number"
                :disabled="isSending"
            />
            <c-input
                v-model="sftpCredentials.username"
                label="SFTP Username"
                type="text"
                placeholder="Enter SFTP username"
                :lazy="false"
                :is-valid="checkIfValid('username')"
                invalid-feedback="Please enter a username"
                autocomplete="off"
                :disabled="isSending"
            />
            <c-input
                v-model="sftpCredentials.password"
                label="SFTP Password"
                type="password"
                placeholder="Enter SFTP password"
                :lazy="false"
                :is-valid="checkIfValid('password')"
                invalid-feedback="Please enter a password"
                autocomplete="off"
                :disabled="isSending"
            />
            <c-input
                v-model="sftpCredentials.remotePath"
                label="SFTP Remote Path"
                type="text"
                placeholder="/home/path/public_html"
                :lazy="false"
                :is-valid="checkIfValid('remotePath')"
                invalid-feedback="Please enter a remote path"
                :disabled="isSending"
            />
          </c-form>
        </c-col>

        <c-col sm="12" md="6" v-if="checkDone || isSending">
          <h3 class="mb-4">Checking the new server</h3>
          <c-row v-for="(check, i) in checks" class="mb-3" :key="`check_${i}`">
            <c-col sm="10">
              <h5>{{check.name}}</h5>
              <div v-if="check.error">{{check.error}}</div>
            </c-col>
            <c-col sm="2">
              <c-icon v-if="!check.success" class="mr-2" name="cil-warning" />
              <c-icon v-else class="mr-2" name="cil-check" />
            </c-col>
          </c-row>
        </c-col>
      </c-row>
    </template>
    <template #footer>
      <c-button v-if="cloneStarted" color="secondary" @click="close">Close</c-button>
      <c-button-group v-else>
        <c-button color="secondary" :disabled="isSending" @click="close">Cancel</c-button>
        <c-button color="primary" :disabled="isSending">
          <c-spinner v-if="isSending" color="success" size="sm" />
          <template v-else>
            <span v-if="!checkPassed" @click="check">Check</span>
            <span v-else @click="showRunClone = true">Clone</span>
          </template>
        </c-button>
      </c-button-group>
    </template>
    <confirm-modal
        :title="`Clone site: ${site.name}`"
        ok-text="Start Clone"
        :show.sync="showRunClone"
    >
      <p>Please confirm that you would like to clone this site to <strong>{{ sftpCredentials.host }}</strong>?</p>
      <p>This action will replace all data and content on that site with your backup of <strong>{{ site.name }}</strong>.</p>
      <template #footer>
        <button :disabled="isRunningClone" type="button" class="btn btn-secondary" @click="showRunClone = false">
          Cancel
        </button>
        <button :disabled="isRunningClone" type="button" class="btn btn-primary" @click="runClone">
          <c-spinner v-if="isRunningClone" color="success" size="sm" />
          <span v-else>Start Clone</span>
        </button>
      </template>
    </confirm-modal>
  </c-modal>
</template>

<script>
import { validationMixin } from 'vuelidate';
import {
  required,
} from 'vuelidate/lib/validators';

import { buildEndpoint } from '@/libs/buildEndPoint';
import ConfirmModal from '../ConfirmModal';

export default {
  name: 'CloneSite',
  components: {
    ConfirmModal,
  },
  mixins: [validationMixin],
  props: {
    show: Boolean,
    client: Object,
    backup: Object,
    site: Object,
  },
  data() {
    return {
      cloneStarted: false,
      isSending: false,
      checkDone: false,
      isRunningClone: false,
      showRunClone: false,
      // sftpCredentials: {
      //   host: 'dikate.wordifysites.com',
      //   port: 22,
      //   username: 'nlcyerbg',
      //   password: 'yhlriizmructsiqm',
      //   remotePath: '/home/nlcyerbg/public_html',
      // },
      sftpCredentials: {
        host: '',
        port: 22,
        username: '',
        password: '',
        remotePath: '',
      },
      checks: [],
      checkPassed: false,
      // sftpCredentials: {
      //   host: null,
      //   port: 22,
      //   username: null,
      //   password: null,
      //   remotePath: null,
      // },
    };
  },
  computed: {
    title() {
      return this.cloneStarted ? 'Cloning is about to get underway!' : `Clone site: ${this.site.name}`;
    },
    isValid() {
      return !this.$v.sftpCredentials.$invalid;
    },
    isDirty() {
      return this.$v.sftpCredentials.$anyDirty;
    },
  },
  validations: {
    sftpCredentials: {
      host: {
        required,
      },
      port: {
        required,
      },
      username: {
        required,
      },
      password: {
        required,
      },
      remotePath: {
        required,
      },
    },
  },
  methods: {
    toggle(event) {
      if (!event) {
        this.close();
      }
    },
    checkIfValid(fieldName) {
      const field = this.$v.sftpCredentials[fieldName];
      if (!field.$dirty) {
        return null;
      }
      return !(field.$invalid || field.$model === '');
    },
    close() {
      this.$v.$reset();
      this.cloneStarted = false;
      this.checkDone = false;
      this.checkPassed = false;

      this.$emit('close');
    },
    check() {
      this.$v.$touch();

      if (!this.isValid) {
        return;
      }
      this.isSending = true;
      this.checks = [];

      this.$http
        .post(buildEndpoint('site/clone/check'), this.sftpCredentials)
        .then((response) => {
          this.isSending = false;
          this.checkDone = true;
          if (response.data.success) {
            const checkResults = response.data.data.checks;
            this.checks = Object.keys(checkResults).map((key) => {
              const { name, success, error } = checkResults[key];
              return {
                name, success, error,
              };
            });
            this.checkPassed = response.data.data.success;
          } else {
            this.checkPassed = false;
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'Error checking SFTP credentials',
            });
          }
        })
        .catch((error) => {
          this.isSending = false;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error,
          });
        });
    },
    runClone() {
      this.$v.$touch();

      if (!this.isValid) {
        return;
      }
      this.isRunningClone = true;

      const { _id: backupId } = this.backup;
      const { _id: siteId } = this.site;

      this.$http
        .post(buildEndpoint('site/clone/run'), { ...this.sftpCredentials, siteId, backupId })
        .then((response) => {
          this.isRunningClone = false;
          if (response.data.success) {
            this.showRunClone = false;
            this.cloneStarted = true;
          } else {
            this.$store.commit('addErrorMessage', {
              header: 'Error',
              message: 'Error starting site clone',
            });
          }
        })
        .catch((error) => {
          this.isRunningClone = false;
          this.$store.commit('addErrorMessage', {
            header: 'Error',
            message: error,
          });
        });
    },
  },
};
</script>
